import React from 'react';
import type { PreviewSiteManifestResponse } from '../../state/api/organisationEngineSlice';

type PreviewSummaryCalloutProps = {
    summary: PreviewSiteManifestResponse['summary'];
};

// Foregrounds the per-action counts (to create / to update / needs attention) so
// what's about to happen is obvious at a glance. Unchanged + total drop to a
// muted footnote because they're informational, not actionable.
export const PreviewSummaryCallout = ({ summary }: PreviewSummaryCalloutProps) => (
    <div data-testid='preview-summary' className='rounded border border-grey-30 bg-white p-5 shadow-sm'>
        <div className='flex flex-wrap items-baseline gap-x-10 gap-y-3'>
            <Headline label='To create' value={summary.new} tone={summary.new > 0 ? 'create' : 'muted'} testId='headline-new' />
            <Headline label='To update' value={summary.update} tone={summary.update > 0 ? 'update' : 'muted'} testId='headline-update' />
            <Headline
                label='Needs attention'
                value={summary.errors}
                tone={summary.errors > 0 ? 'danger' : 'muted'}
                testId='headline-errors'
            />
        </div>
        <div className='mt-4 flex flex-wrap gap-x-6 gap-y-1 text-sm text-grey-70'>
            <span>
                <span className='font-semibold text-grey-100' data-testid='summary-unchanged'>
                    {summary.unchanged}
                </span>{' '}
                unchanged
            </span>
            <span data-testid='summary-total-label'>
                of {summary.total} total {summary.total === 1 ? 'row' : 'rows'} in the manifest
            </span>
        </div>
    </div>
);

type Tone = 'create' | 'update' | 'danger' | 'muted';

const TONE_COLOR: Record<Tone, string> = {
    create: 'text-green-100',
    update: 'text-blue-100',
    danger: 'text-red-100',
    muted: 'text-grey-70',
};

const Headline = ({ label, value, tone, testId }: { label: string; value: number; tone: Tone; testId: string }) => (
    <div className='flex flex-col'>
        <span className='text-xs uppercase tracking-wide text-grey-70'>{label}</span>
        <span className={`text-3xl font-semibold ${TONE_COLOR[tone]}`} data-testid={testId}>
            {value}
        </span>
    </div>
);
